import { isRecord } from '../common/is-record';
import type { PromptOfTheDayMetadata, PromptOfTheDayPrompt } from '../types';

const parseNonEmptyString = (value: unknown): string | null => {
	if (typeof value !== 'string') {
		return null;
	}

	const trimmed = value.trim();
	return trimmed === '' ? null : trimmed;
};

const parseNonEmptyStrings = (value: unknown): readonly string[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const strings = value.map(parseNonEmptyString).filter((item): item is string => item !== null);
	return strings.length > 0 ? strings : undefined;
};

// Optional and best-effort: anything malformed is dropped, never the prompt it belongs to.
const parseMetadata = (value: unknown): PromptOfTheDayMetadata | undefined => {
	if (!isRecord(value) || !isRecord(value.highlights)) {
		return undefined;
	}

	const shortPrompt = parseNonEmptyStrings(value.highlights.shortPrompt);
	return shortPrompt !== undefined ? { highlights: { shortPrompt } } : undefined;
};

const parsePrompt = (value: unknown): PromptOfTheDayPrompt | null => {
	if (!isRecord(value)) {
		return null;
	}

	const shortPrompt = parseNonEmptyString(value.shortPrompt);
	if (shortPrompt === null) {
		return null;
	}

	const title = parseNonEmptyString(value.title);
	const expandedPrompt = parseNonEmptyString(value.expandedPrompt);
	const sources = parseNonEmptyStrings(value.sources);
	const metadata = parseMetadata(value.metadata);

	return {
		shortPrompt,
		...(title !== null && { title }),
		...(expandedPrompt !== null && { expandedPrompt }),
		...(sources !== undefined && { sources }),
		...(metadata !== undefined && { metadata }),
	};
};

export const parsePromptsFromOutput = (output: unknown): readonly PromptOfTheDayPrompt[] | null => {
	if (!isRecord(output) || !Array.isArray(output.prompts)) {
		return null;
	}

	const prompts = output.prompts.map(parsePrompt);
	return prompts.length > 0 && prompts.every((prompt) => prompt !== null)
		? (prompts as readonly PromptOfTheDayPrompt[])
		: null;
};
